import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Space
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { investmentsApi } from './investmentsApi';
import {
  findValuationFormErrors, friendlyErrorFor, formatRupee
} from './investmentsHelpers';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Valuation modal (Stage 6B.9E).
//
// REGISTER-ONLY. Updates investments.current_value and writes a
// 'valuation' transaction row. NO voucher is posted (MTM accounting
// is deferred). NO Approval-MFA gate.
// ─────────────────────────────────────────────────────────────────

export default function InvestmentValuationModal({
  open, investment, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      valuationDate: dayjs(),
      newCurrentValue: investment ? Number(investment.currentValue) || 0 : 0,
      remarks: ''
    });
  }, [open, form, investment]);

  const save = useCallback(async () => {
    if (!investment) return;
    try {
      const values = await form.validateFields();
      const body = {
        valuationDate:   values.valuationDate.format('YYYY-MM-DD'),
        newCurrentValue: Number(values.newCurrentValue) || 0,
        remarks:         values.remarks || null
      };
      const errs = findValuationFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      setBusy(true);
      await investmentsApi.valuation(investment.id, body);
      message.success('Investment value updated.');
      onSaved && onSaved();
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [investment, form, onSaved]);

  if (!investment) return null;

  return (
    <Modal
      title={`Update Current Value — ${investment.investmentCode} · ${investment.investmentName}`}
      open={open}
      onCancel={onClose}
      width={620}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={save}>
            Save Valuation
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Valuation Date" name="valuationDate"
              rules={[{ required: true, message: 'Valuation date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="New Current Value (₹)" name="newCurrentValue"
              rules={[{ required: true, message: 'New current value is required.' }]}>
              <InputNumber min={0} step={1000} style={{ width: '100%' }}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="Optional — e.g. NAV update on 31-Mar from broker statement" />
        </Form.Item>
      </Form>

      <Alert type="info" showIcon
        message="Register-only update"
        description={
          <span>
            This updates the investment’s current value to {' '}
            ₹ {formatRupee(form.getFieldValue('newCurrentValue') || 0)}
            {' '}and records a <code>valuation</code> transaction. <strong>No accounting entry</strong> is posted —
            mark-to-market gains/losses are deferred to a later stage.
          </span>
        } />
    </Modal>
  );
}
